import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MINIMO_BUSQUEDA,
  RETARDO_BUSQUEDA,
  debeEnviar,
  debeResincronizar,
  esBusquedaInsuficiente,
  hayBusquedaPendiente,
  terminoDeBusqueda,
} from '@/lib/busqueda';
import { useDebouncedCallback } from './use-debounce';

/**
 * El cerebro de la barra de búsqueda: uno solo para toda la aplicación.
 *
 * Acá está el estado —el borrador de lo tecleado, el reloj del freno, el
 * término que ya se mandó—; QUÉ se manda y CUÁNDO lo decide `@/lib/busqueda`,
 * que es un módulo puro y por eso se prueba con una tabla. La división no es
 * ceremonia: las cuatro reglas son lo que se olvida al copiar un campo de una
 * pantalla a otra, y en un módulo sin React se leen todas juntas.
 */
export function useBusqueda({
  valor,
  onCambiar,
  retardo = RETARDO_BUSQUEDA,
  minimo = MINIMO_BUSQUEDA,
}: UseBusquedaOptions): Busqueda {
  const [texto, setTexto] = useState(valor);

  // El último término que pasó por acá, venga de una tecla o de afuera. Es lo
  // que distingue «cambió porque yo lo mandé» de «cambió la URL por otra vía»,
  // y de esa distinción depende que el borrador no se pise (ver el efecto).
  const ultimo = useRef(valor);

  // `onCambiar` es una función nueva en cada render de la pantalla que nos usa.
  // Guardarla en una referencia es lo que deja que `enviar` sea estable, y sin
  // eso el freno se reiniciaría con cada render y no frenaría nada.
  const onCambiarRef = useRef(onCambiar);
  useEffect(() => {
    onCambiarRef.current = onCambiar;
  }, [onCambiar]);

  const enviar = useCallback((siguiente: string) => {
    if (!debeEnviar(siguiente, ultimo.current)) return;
    ultimo.current = siguiente;
    onCambiarRef.current(siguiente);
  }, []);

  const minimoRef = useRef(minimo);
  minimoRef.current = minimo;
  const empujar = useDebouncedCallback(
    (borrador: string) => enviar(terminoDeBusqueda(borrador, minimoRef.current)),
    retardo,
  );

  // Seguir a la URL cuando cambia POR OTRA VÍA. Ver `debeResincronizar`: la
  // condición no es «difieren», y por qué no lo es está explicado ahí.
  useEffect(() => {
    if (!debeResincronizar(valor, ultimo.current)) return;
    ultimo.current = valor;
    setTexto(valor);
  }, [valor]);

  const escribir = useCallback(
    (siguiente: string) => {
      setTexto(siguiente);
      if (retardo > 0) empujar(siguiente);
      else enviar(terminoDeBusqueda(siguiente, minimoRef.current));
    },
    [empujar, enviar, retardo],
  );

  // Regla 4: sin freno. El que espera es quien escribe, no quien borra — y
  // dejar el reloj corriendo mandaría el término viejo un tercio de segundo
  // después de haberlo limpiado.
  const limpiar = useCallback(() => {
    empujar.cancel();
    setTexto('');
    enviar('');
  }, [empujar, enviar]);

  return {
    texto,
    escribir,
    limpiar,
    pendiente: hayBusquedaPendiente(texto, valor, minimo),
    insuficiente: esBusquedaInsuficiente(texto, minimo),
    minimo,
  };
}

export interface UseBusquedaOptions {
  /** El término que el servidor está contestando. Suele ser `list.params.q`. */
  valor: string;
  /** Qué hacer con un término nuevo: escribir la URL, mover un filtro. */
  onCambiar: (termino: string) => void;
  /** Milisegundos desde la última tecla. `0` manda en cada tecla. */
  retardo?: number;
  /** Mínimo de caracteres para salir a preguntar. */
  minimo?: number;
}

export interface Busqueda {
  /** Lo tecleado. Controla el campo, así que va al ritmo del teclado. */
  texto: string;
  escribir: (texto: string) => void;
  /** Vacía el campo y pregunta por la lista entera, sin esperar el freno. */
  limpiar: () => void;
  /** Lo tecleado todavía no llegó al servidor: la lista de abajo es la de antes. */
  pendiente: boolean;
  /** Hay algo escrito y no alcanza el mínimo, así que no se preguntó nada. */
  insuficiente: boolean;
  /** Cuántos caracteres hacen falta. Para decirlo en pantalla. */
  minimo: number;
}
